import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, formatBytes } from '../lib/api.js';

type Processing = Awaited<ReturnType<typeof api.adminProcessing>>;

function elapsed(since: number | null): string {
  if (!since) return '—';
  const seconds = Math.floor((Date.now() - since) / 1000);
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

/**
 * Live view of the processing pipeline.
 *
 * Pipeline state is read from the database rather than the queues. A job that
 * was never successfully enqueued leaves no trace in Redis — which is exactly
 * the failure that once hid every missing thumbnail while all four queues
 * reported healthy and empty.
 */
export function ProcessingPanel(): ReactNode {
  const [data, setData] = useState<Processing | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [live, setLive] = useState(true);

  const load = useCallback(() => {
    api
      .adminProcessing()
      .then(setData)
      .catch((err: Error) => setNotice(err.message));
  }, []);

  useEffect(() => {
    load();
    if (!live) return;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [load, live]);

  const retry = (kind: 'ingest' | 'vision' | 'transcript'): void => {
    setBusy(true);
    void api
      .adminRetry(kind)
      .then((r) => {
        setNotice(
          r.retried === 0
            ? `Nothing was in a failed state for ${kind}.`
            : `Re-queued ${r.retried} ${kind} job(s).`,
        );
        load();
      })
      .catch((err: Error) => setNotice(err.message))
      .finally(() => setBusy(false));
  };

  const p = data?.pipeline;
  const t = data?.throughput;

  /**
   * Re-runs ingest for everything with no thumbnail.
   *
   * Forced on the server, and it has to be: these assets are 'ready', because
   * ingest did run and simply produced nothing, and an unforced reindex skips
   * 'ready' by design. Without the force this button would report success and
   * queue nothing at all.
   */
  const requeueMissingThumbnails = useCallback(async (): Promise<void> => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.reindex({ missingThumbnails: true, stages: ['ingest'] });
      setNotice(
        res.queued > 0
          ? `Queued ${res.queued} file(s) for thumbnail generation.` +
              (res.truncated ? ' More remain — run it again when these finish.' : '')
          : 'Nothing was queued. Every file without a thumbnail is already in the queue.',
      );
      load();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Could not queue those files');
    } finally {
      setBusy(false);
    }
  }, [load]);

  const stage = (
    label: string,
    done: number,
    pending: number,
    failed: number,
    kind: 'ingest' | 'vision' | 'transcript',
  ): ReactNode => {
    const total = done + pending + failed;
    const pct = total > 0 ? Math.round((done / total) * 100) : 100;
    return (
      <div style={{ marginBottom: 14 }}>
        <div className="flex" style={{ marginBottom: 4 }}>
          <strong style={{ fontSize: 13 }}>{label}</strong>
          <div className="spacer" />
          <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
            {done} done
            {pending > 0 && ` · ${pending} waiting`}
            {failed > 0 && (
              <span style={{ color: 'var(--danger)' }}> · {failed} failed</span>
            )}
          </span>
          {failed > 0 && (
            <button className="btn sm" disabled={busy} onClick={() => retry(kind)}>
              Retry {failed}
            </button>
          )}
        </div>
        <div className="progress">
          <div
            style={{
              width: `${pct}%`,
              background: failed > 0 ? 'var(--warning)' : 'var(--success)',
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {notice && <div className="notice">{notice}</div>}

      {(() => {
        // The pipeline counts come from the database; the queue counts come
        // from Redis. When the database says work is outstanding and the
        // queues are empty, nothing is going to happen on its own -- and that
        // combination is otherwise invisible, since each half looks fine.
        const dbPending =
          (p?.ingest_pending ?? 0) + (p?.vision_pending ?? 0) + (p?.transcript_pending ?? 0);
        const queued = Object.values(data?.queues ?? {}).reduce(
          (sum, c) => sum + (c.waiting ?? 0) + (c.active ?? 0) + (c.delayed ?? 0),
          0,
        );

        if (dbPending === 0 || queued > 0 || !data?.queues) return null;

        return (
          <div className="notice error">
            <strong>{dbPending} file(s) are waiting, but nothing is queued.</strong>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              The database says this work is outstanding while Redis holds no jobs for it, so
              it will not start on its own. Restarting the worker re-queues everything in this
              state:
              <div style={{ fontFamily: 'monospace', marginTop: 4 }}>
                systemctl restart mediaapp-worker
              </div>
              If it comes back, the worker is not consuming the queues — check that it is
              running at all.
            </div>
          </div>
        );
      })()}

      <div className="panel">
        <div className="flex" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Pipeline</h3>
          <div className="spacer" />
          <label className="flex" style={{ fontSize: 12, color: 'var(--text-dim)', gap: 6 }}>
            <input
              type="checkbox"
              checked={live}
              onChange={(e) => setLive(e.target.checked)}
              style={{ width: 'auto' }}
            />
            auto-refresh
          </label>
          <button className="btn sm" onClick={load}>
            Refresh
          </button>
        </div>

        {p ? (
          <>
            {stage('Ingest — metadata and thumbnails', p.ingest_ready ?? 0, p.ingest_pending ?? 0, p.ingest_failed ?? 0, 'ingest')}
            {stage('Vision — tags, objects, faces', p.vision_done ?? 0, (p.vision_pending ?? 0) + (p.vision_running ?? 0), p.vision_failed ?? 0, 'vision')}
            {stage('Transcription — speech to text', p.transcript_done ?? 0, p.transcript_pending ?? 0, p.transcript_failed ?? 0, 'transcript')}

            {(p.missing_thumbnail ?? 0) > 0 && (
              <div className="notice" style={{ marginTop: 10 }}>
                <div>
                  {p.missing_thumbnail} image/video file(s) have no thumbnail. Ingest
                  finished for these without producing one — usually because a decoder
                  was missing at the time, as raw and HEIC were before the worker could
                  read them.
                </div>
                <button
                  className="btn sm"
                  style={{ marginTop: 8 }}
                  disabled={busy}
                  onClick={() => void requeueMissingThumbnails()}
                >
                  {busy ? 'Queueing…' : `Regenerate ${p.missing_thumbnail} thumbnail(s)`}
                </button>
              </div>
            )}
          </>
        ) : (
          <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>No pipeline data.</p>
        )}
      </div>

      <div className="panel">
        <h3>Throughput</h3>
        <dl className="kv">
          <dt>Last hour</dt>
          <dd>{t?.last_hour ?? 0} files</dd>
          <dt>Last 24 hours</dt>
          <dd>
            {t?.last_day ?? 0} files
            {t?.bytes_last_day ? ` (${formatBytes(Number(t.bytes_last_day))})` : ''}
          </dd>
          <dt>Last 7 days</dt>
          <dd>{t?.last_week ?? 0} files</dd>
        </dl>
      </div>

      <div className="panel">
        <h3>Running now ({data?.active.length ?? 0})</h3>
        {!data?.active.length ? (
          <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>Nothing is processing.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 110 }}>Stage</th>
                <th>Asset</th>
                <th style={{ width: 90 }}>Running</th>
                <th style={{ width: 70 }}>Attempt</th>
              </tr>
            </thead>
            <tbody>
              {data.active.map((job) => (
                <tr key={`${job.queue}-${job.id}`}>
                  <td>{job.queue}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {job.assetId ? (
                      <Link to={`/asset/${job.assetId}`} style={{ color: 'var(--accent)' }}>
                        {job.assetId.slice(0, 8)}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{elapsed(job.startedAt)}</td>
                  <td>{job.attempts + 1}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.stuck.length > 0 && (
        <div className="panel">
          <h3 style={{ color: 'var(--warning)' }}>Stuck ({data.stuck.length})</h3>
          <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 0 }}>
            Unprocessed for over an hour. Either the worker is down, or these were never
            queued — the recovery sweep picks those up on worker restart.
          </p>
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th style={{ width: 100 }}>Ingest</th>
                <th style={{ width: 100 }}>Vision</th>
                <th style={{ width: 140 }}>Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {data.stuck.map((asset) => (
                <tr key={asset.id}>
                  <td>
                    <Link to={`/asset/${asset.id}`} style={{ color: 'var(--accent)' }}>
                      {asset.filename}
                    </Link>
                  </td>
                  <td>{asset.status}</td>
                  <td>{asset.vision_status}</td>
                  <td>{new Date(asset.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.failures.length > 0 && (
        <div className="panel">
          <h3 style={{ color: 'var(--danger)' }}>Recent failures</h3>
          <table>
            <thead>
              <tr>
                <th style={{ width: 100 }}>Stage</th>
                <th>Error</th>
                <th style={{ width: 70 }}>Tries</th>
              </tr>
            </thead>
            <tbody>
              {data.failures.map((job) => (
                <tr key={`${job.queue}-${job.id}`}>
                  <td>{job.queue}</td>
                  <td style={{ fontSize: 11, overflowWrap: 'anywhere' }}>
                    {job.failedReason ?? 'unknown'}
                    {job.assetId && (
                      <div>
                        <Link
                          to={`/asset/${job.assetId}`}
                          style={{ color: 'var(--accent)', fontSize: 11 }}
                        >
                          view asset
                        </Link>
                      </div>
                    )}
                  </td>
                  <td>{job.attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="panel">
        <h3>Queues</h3>
        {!data?.queues ? (
          <p style={{ color: 'var(--danger)', fontSize: 13 }}>
            Redis is not reachable — nothing can be queued.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Queue</th>
                <th>Waiting</th>
                <th>Active</th>
                <th>Delayed</th>
                <th>Failed</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.queues).map(([name, counts]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{counts.waiting ?? 0}</td>
                  <td>{counts.active ?? 0}</td>
                  <td>{counts.delayed ?? 0}</td>
                  <td style={{ color: counts.failed ? 'var(--danger)' : undefined }}>
                    {counts.failed ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
